import React, { useEffect, useRef, useState } from 'react'
import ImageContentful from './image-contentful'
import { cx } from '@/utils/strings'

export interface ImageZoomDialogProps {
  isOpen: boolean
  onClose: () => void
  invertSvgForDarkMode?: boolean
  image: any
  darkModeImage?: any
  noOptimization?: boolean
  showCaption?: boolean
  imageTitle?: string
  imageDescription?: string
  triggerElement?: HTMLElement | null
}

export default function ImageZoomDialog({
  isOpen,
  onClose,
  invertSvgForDarkMode = false,
  image,
  darkModeImage,
  noOptimization,
  showCaption,
  triggerElement,
}: ImageZoomDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const fields = image || {}
  const [isAnimating, setIsAnimating] = useState(false)
  const [shouldRender, setShouldRender] = useState(false)
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null)
  const [isScrollClosing, setIsScrollClosing] = useState(false)
  const scrollStartRef = useRef<{ x: number; y: number } | null>(null)
  const wheelAccumulatedRef = useRef({ y: 0 })

  // Handle animation states
  useEffect(() => {
    if (isOpen) {
      // Capture trigger element position
      if (triggerElement) {
        setTriggerRect(triggerElement.getBoundingClientRect())
      }

      setShouldRender(true)

      // Ensure we start in "closed" state so animation can when opening
      setIsAnimating(false)

      // double requestAnimationFrame to ensure DOM is fully rendered (js executed and dom painted) then set animation
      const rafId = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true)
        })
      })

      return () => cancelAnimationFrame(rafId)
    } else {
      // Start close animation
      setIsAnimating(false)
      // Use faster timeout when closing due to scroll
      const timeout = isScrollClosing ? 200 : 400
      const timer = setTimeout(() => {
        setShouldRender(false)
        setTriggerRect(null)
        setIsScrollClosing(false) // Reset flag
      }, timeout)
      return () => clearTimeout(timer)
    }
  }, [isOpen, triggerElement, isScrollClosing])

  // Handle escape key and scroll events for modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    const closeModalWithScrollAnimation = () => {
      if (triggerElement) {
        const newRect = triggerElement.getBoundingClientRect()
        setTriggerRect(newRect)
      }
      setIsScrollClosing(true)
      onClose()
    }

    const handleScroll = () => {
      if (isOpen) {
        // Initialize scroll tracking if not already set
        if (!scrollStartRef.current) {
          scrollStartRef.current = { x: window.scrollX, y: window.scrollY }
          return
        }
        const totalScrollY = Math.abs(window.scrollY - scrollStartRef.current.y)
        // close on intent
        if (totalScrollY > 100) {
          closeModalWithScrollAnimation()
        }
      }
    }

    const handleWheel = (e: WheelEvent) => {
      if (isOpen) {
        wheelAccumulatedRef.current.y += Math.abs(e.deltaY)
        // close on intent
        if (wheelAccumulatedRef.current.y > 100) {
          closeModalWithScrollAnimation()
        }
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (isOpen && e.touches.length > 0) {
        const touch = e.touches[0]

        // Initialize touch tracking if not already set
        if (!scrollStartRef.current) {
          scrollStartRef.current = { x: touch.clientX, y: touch.clientY }
          return
        }
        const totalMoveY = Math.abs(touch.clientY - scrollStartRef.current.y)
        // close on intent
        if (totalMoveY > 100) {
          closeModalWithScrollAnimation()
        }
      }
    }

    if (isOpen) {
      // Disable scroll when dialog is open
      document.body.style.overflow = 'hidden'

      // Reset scroll tracking when modal opens
      scrollStartRef.current = null
      wheelAccumulatedRef.current = { y: 0 }

      // Add event listeners
      document.addEventListener('keydown', handleKeyDown)
      document.addEventListener('scroll', handleScroll, true) // Use capture phase
      document.addEventListener('wheel', handleWheel, { passive: true })
      document.addEventListener('touchmove', handleTouchMove, { passive: true })
    }

    return () => {
      document.body.removeAttribute('style')

      // Reset scroll tracking when cleaning up
      scrollStartRef.current = null
      wheelAccumulatedRef.current = { y: 0 }

      // Remove event listeners
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('scroll', handleScroll, true)
      document.removeEventListener('wheel', handleWheel)
      document.removeEventListener('touchmove', handleTouchMove)
    }
  }, [isOpen, onClose, triggerElement])

  return (
    <dialog
      ref={dialogRef}
      open
      className={cx(
        `fixed inset-0 cursor-zoom-out z-50 border-0 p-0 w-full h-full transition-all ease-out`,
        `${isScrollClosing ? 'duration-1200' : 'duration-400'}`,
        `${isAnimating ? 'bg-white dark:bg-secondary-900 bg-opacity-100' : 'bg-transparent bg-opacity-0'}`,
        !shouldRender && 'hidden'
      )}
      aria-label="Image zoom view"
      onClick={onClose}
    >
      <button
        className={`absolute top-6 right-6 flex items-center justify-center z-20 transition-all duration-400 ease-out ${
          isAnimating ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
        }`}
        onClick={onClose}
        aria-label="Close zoom"
      >
        <span className="bwi-font text-3xl text-secondary-500 dark:text-secondary-200">&#xe93d;</span>
      </button>

      {/* Image container with position and scale animation */}

      <figure>
        <div
          className={`absolute z-10 transition-all ease-out ${isScrollClosing ? 'duration-700' : 'duration-400'}`}
          style={{
            left: triggerRect ? `${triggerRect.left}px` : '0px',
            top: triggerRect ? `${triggerRect.top}px` : '0px',
            width: triggerRect ? `${triggerRect.width}px` : '200px',
            height: triggerRect ? `${triggerRect.height}px` : '200px',
            transformOrigin: 'top left',
            transform: isAnimating
              ? (() => {
                  // Get the natural image dimensions from Contentful
                  const naturalWidth = image.file.details.image.width
                  const naturalHeight = image.file.details.image.height

                  // Calculate scale to fit 90% of viewport
                  const viewportScale = Math.min(
                    (window.innerWidth * 0.9) / (triggerRect?.width || 200),
                    (window.innerHeight * 0.9) / (triggerRect?.height || 200)
                  )

                  // Calculate scale to not exceed natural size
                  const naturalScale = Math.min(
                    naturalWidth / (triggerRect?.width || 200),
                    naturalHeight / (triggerRect?.height || 200)
                  )

                  // Use the smaller of the two scales (don't go beyond natural size)
                  const scale = Math.min(viewportScale, naturalScale)

                  // Calculate final scaled dimensions
                  const scaledWidth = (triggerRect?.width || 200) * scale
                  const scaledHeight = (triggerRect?.height || 200) * scale
                  // Calculate translation to center the scaled image
                  const translateX = (window.innerWidth - scaledWidth) / 2 - (triggerRect?.left || 0)
                  const translateY = (window.innerHeight - scaledHeight) / 2 - (triggerRect?.top || 0)
                  return `translate(${translateX}px, ${translateY}px) scale(${scale})`
                })()
              : 'translate(0px, 0px) scale(1)',
          }}
        >
          <div className="w-full h-full flex items-center justify-center">
            <ImageContentful
              invertSvgForDarkMode={invertSvgForDarkMode}
              {...fields}
              darkModeImage={darkModeImage}
              loading="eager"
              noOptimization={noOptimization}
              isZoomed={true}
            />
          </div>
        </div>
        {showCaption && (
          <figcaption
            className={cx(
              'absolute z-10 bottom-0 left-0 right-0 w-full bg-primary-700 text-secondary-50 text-xl text-center py-2 px-4 transition-all duration-400 ease-out',
              isAnimating ? 'opacity-85 translate-y-0' : 'opacity-0 translate-y-4'
            )}
          >
            <cite>{fields.title}</cite>
            {fields.title && fields.description && <> &#8212; </>}
            {fields.description}
          </figcaption>
        )}
      </figure>
    </dialog>
  )
}
